import type { DirectusCompany } from '@/types/directus-types'
import generateUniqueLocalID from '../utils/generateUniqueLocalID'

export default class Fleet {
  // -------------------------------------------------------------------- //
  // ------------------------- Global class state ----------------------- //
  // -------------------------------------------------------------------- //

  // global record of class instance ids this session.
  static usedIds: string[] = []

  // -------------------------------------------------------------------- //
  // ------------------------------- State ------------------------------ //
  // -------------------------------------------------------------------- //

  /** local unique id. */
  localId: string

  /** external id for this fleet */
  external_id: string

  /** Flag that represents if a driver is allowed to manually match or change vehicle data. */
  driverEditAccess: boolean

  /** Display name for this fleet. */
  name?: string

  /** Fleets's logo asset file UUID. */
  logo?: string

  /** Flag that represents if a drivers are allowed to book vehicles for this fleet. */
  allowVehicleBooking: boolean

  /** Flag that represents if a drivers are allowed to book private chargers for this fleet. */
  allowPrivateChargerBooking: boolean

  /** Flag that represents if vehicle bookings for this fleet are handled by an external service. */
  thirdPartyVehicleBookingOnly: boolean

  /** URL for external vehicle booking service. */
  thirdPartyVehicleBookingUrl?: string

  // -------------------------------------------------------------------- //
  // --------------------------- Constructor ---------------------------- //
  // -------------------------------------------------------------------- //

  constructor({
    localId = undefined,
    driverEditAccess = false,
    name = undefined,
    logo = undefined,
    allowVehicleBooking = false,
    allowPrivateChargerBooking = false,
    thirdPartyVehicleBookingOnly = false,
    thirdPartyVehicleBookingUrl = undefined,
    external_id = 'singleton' //TODO remove 
  }: {
    localId?: string
    driverEditAccess?: boolean
    name?: string
    logo?: string
    allowVehicleBooking?: boolean
    allowPrivateChargerBooking?: boolean
    thirdPartyVehicleBookingOnly?: boolean
    thirdPartyVehicleBookingUrl?: string
    external_id: string
  }) {
    this.external_id = external_id
    this.localId = localId ?? generateUniqueLocalID(Fleet.usedIds, 'fleet')
    this.driverEditAccess = driverEditAccess
    this.name = name
    this.logo = logo
    this.allowVehicleBooking = allowVehicleBooking
    this.allowPrivateChargerBooking = allowPrivateChargerBooking
    this.thirdPartyVehicleBookingOnly = thirdPartyVehicleBookingOnly
    this.thirdPartyVehicleBookingUrl = thirdPartyVehicleBookingUrl

    // add id to list of used unique ids
    Fleet.usedIds.push(this.localId)
  }

  static fromDirectusData(data: DirectusCompany) {
    return new Fleet({
      external_id: data.id,
      driverEditAccess: data.ManualVehicleMatching ?? undefined,
      name: data.Name ?? undefined,
      logo: data.Logo ?? undefined,
      allowVehicleBooking: data.allow_vehicle_booking ?? undefined,
      allowPrivateChargerBooking: data.allow_private_charger_booking ?? undefined,
      thirdPartyVehicleBookingOnly: data.third_party_vehicle_booking_only ?? undefined,
      thirdPartyVehicleBookingUrl: data.third_party_vehicle_booking_url ?? undefined,
    })
  }

  // -------------------------------------------------------------------- //
  // ------------------------------ Getters ----------------------------- //
  // -------------------------------------------------------------------- //

  /** Returns the src path for this fleets logo pic if they have one. */
  public get logoSrc(): string | undefined {
    return this.logo
  }

  // -------------------------------------------------------------------- //
  // ------------------------------ Methods ----------------------------- //
  // -------------------------------------------------------------------- //
}
